/**
 * Shared colour constants for pitch rendering across strack and the coding tool.
 *
 * Both the Babylon.js 3D renderer (strack) and the 2D canvas renderer (coding
 * tool) need the same semantic colours for trajectories, players, balls, drill
 * regions, and events. This module is the single source of truth.
 *
 * For Babylon.js consumers that need Color3, use the `hexToColor3` helper.
 */

export const STRACK_COLOURS = {
  pitch: {
    background: '#111111',
    backgroundAlt: '#1D2B46',
    lines: '#ffffff',
    linesRgba: 'rgba(255, 255, 255, 0.9)',
    americanFootballBg: '#233657',
    canadianFootballBg: '#233657',
    soccerBg: '#233657'
  },
  ball: {
    standard: '#facc15',
    inPlay: '#f97316',
    standard2D: '#E4F6F8',
    inPlay2D: '#04bcf7'
  },
  player: {
    default: '#3b82f6',
    teamAFallback: '#ffff33',
    teamBFallback: '#ff9f21'
  },
  trajectory: {
    sport: '#f59e0b',
    previous: '#0d9488',
    default: '#7DF9FF',
    launch: '#22c55e',
    end: '#ef4444',
    highlighted: '#ffff00',
    success: '#00FF00',
    selected: '#ffff00'
  },
  drill: {
    region: '#f59e0b',
    regionFill: 'rgba(245, 158, 11, 0.08)',
    goalPost: '#ef4444'
  },
  event: {
    saved: '#16a34a',
    unsaved: '#ef4444',
    previousUser: '#0d9488',
    default: '#6b7280',
    byType: {
      SoccerPass: '#3b82f6',
      SoccerShotAtGoal: '#ef4444',
      SoccerDribble: '#8b5cf6'
    } as Record<string, string>,
    palette: [
      '#ef4444',
      '#8b5cf6',
      '#06b6d4',
      '#f59e0b',
      '#10b981',
      '#ec4899',
      '#6366f1',
      '#14b8a6'
    ]
  },
  selection: '#ffffff',
  recording: '#ef4444',
  canvas: {
    marker: '#facc15',
    hoverLabel: '#e4e4e7',
    debug: '#a1ffa1'
  }
} as const

/** Convert hex colour to Babylon.js Color3 normalised 0-1 RGB values. */
export function hexToColor3(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace('#', '')
  return {
    r: parseInt(h.substring(0, 2), 16) / 255,
    g: parseInt(h.substring(2, 4), 16) / 255,
    b: parseInt(h.substring(4, 6), 16) / 255
  }
}
